import { useEffect, useState } from "react";

import { isMobileAppRuntime } from "../utils/isMobileAppRuntime";

declare global {
  interface Window {
    IS_MOBILE_APP?: boolean;
    KAN_APP_VERSION?: string;
    EXPO_PUSH_TOKEN?: string;
  }
}

function getUserAgent(): string {
  if (typeof navigator === "undefined") return "";
  return navigator.userAgent || "";
}

/** Android phones/tablets (WebView or Chrome). */
function isAndroidUserAgent(ua: string): boolean {
  return /Android/i.test(ua);
}

/** iPhone / iPod touch, or iPad (including iPadOS 13+ “desktop” Safari UA). */
function isIosUserAgent(ua: string): boolean {
  if (/iPhone|iPad|iPod/i.test(ua)) return true;
  if (
    typeof navigator !== "undefined" &&
    navigator.platform === "MacIntel" &&
    navigator.maxTouchPoints > 1
  ) {
    return true;
  }
  return false;
}

export type MobileAppPlatform = {
  /** Injected WebView (`window.IS_MOBILE_APP === true`). */
  isMobileApp: boolean;
  /** WebView on Android. */
  isMobileAppAndroid: boolean;
  /** WebView on iOS / iPadOS. */
  isMobileAppIos: boolean;
  /**
   * Regular browser (not app WebView) and UA is not Android.
   * Includes iOS Safari, iPadOS, and desktop (macOS, Windows, …).
   * For an App Store CTA aimed only at iPhone/iPad users, prefer
   * `isNotMobileAppAndroid && isIosUserAgent(getUserAgent())` or add your own check.
   */
  isNotMobileAppAndroid: boolean;
};

const initialPlatform: MobileAppPlatform = {
  isMobileApp: false,
  isMobileAppAndroid: false,
  isMobileAppIos: false,
  isNotMobileAppAndroid: false,
};

function computePlatform(): MobileAppPlatform {
  if (typeof window === "undefined") return initialPlatform;

  const isMobileApp = isMobileAppRuntime();
  const ua = getUserAgent();
  const android = isAndroidUserAgent(ua);

  const ios = isIosUserAgent(ua);

  return {
    isMobileApp,
    isMobileAppAndroid: isMobileApp && android,
    isMobileAppIos: isMobileApp && ios,
    isNotMobileAppAndroid: !isMobileApp && !android,
  };
}

/**
 * WebView vs browser, plus OS hints for Android / iOS WebViews.
 * Use `isNotMobileAppAndroid` for “show App Store” only if you accept desktop;
 * narrow with iOS UA when you want mobile Safari / iPad only.
 */
export const useMobileAppPlatform = (): MobileAppPlatform => {
  const [platform, setPlatform] = useState<MobileAppPlatform>(() =>
    typeof window !== "undefined" ? computePlatform() : initialPlatform,
  );

  useEffect(() => {
    const refresh = () => setPlatform(computePlatform());
    refresh();
    const t = window.setTimeout(refresh, 100);
    return () => window.clearTimeout(t);
  }, []);

  return platform;
};

/**
 * True when the site runs inside the native app WebView (`window.IS_MOBILE_APP`).
 */
export const useIsMobileApp = (): boolean => {
  return useMobileAppPlatform().isMobileApp;
};

/**
 * Hook to get the mobile app version if available
 * @returns {string | null} App version string or null if not in app
 */
export const useMobileAppVersion = (): string | null => {
  const [appVersion, setAppVersion] = useState<string | null>(null);

  useEffect(() => {
    const checkAppVersion = () => {
      if (isMobileAppRuntime()) {
        setAppVersion(window.KAN_APP_VERSION ?? null);
      }
    };

    checkAppVersion();
    const timeout = setTimeout(checkAppVersion, 100);

    return () => clearTimeout(timeout);
  }, []);

  return appVersion;
};

/**
 * Hook to get the Expo push token if available
 * @returns {string | null} Expo push token or null if not available
 */
export const useExpoPushToken = (): string | null => {
  const [pushToken, setPushToken] = useState<string | null>(null);

  useEffect(() => {
    const checkPushToken = () => {
      if (typeof window !== "undefined" && window.EXPO_PUSH_TOKEN) {
        setPushToken(window.EXPO_PUSH_TOKEN);
      }
    };

    checkPushToken();
    const timeout = setTimeout(checkPushToken, 100);

    return () => clearTimeout(timeout);
  }, []);

  return pushToken;
};
